/**
 * SMS 2FA login challenge (letter-first Phase 8).
 *
 * Shown after password login for accounts that enrolled text-message
 * codes and have no TOTP (TOTP wins when both are on). Mirrors the TOTP
 * verification screen's post-success handling: same token storage, same
 * post-auth routing across all three onboarding generations.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { routeToPendingInvitationIfAny } from '../../navigation/pendingInvitation';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Alert,
  Switch,
  Platform,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import {
  pickPostAuthTarget,
  wizardScreenForStep,
  letterScreenForStep,
} from '../../navigation/postAuthRoute';
import { analytics, AnalyticsEvent } from '../../services/analytics';
import {
  setAuthToken,
  setRefreshToken,
  setDeviceTrustToken,
} from '../../services/auth/tokenStorage';
import { securityService } from '../../services/securityService';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';

const logger = createLogger('SmsVerification');

type SmsRouteProp = RouteProp<
  { params: { userId: string; email: string; secondFactorTicket?: string } },
  'params'
>;

export const SmsVerificationScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<SmsRouteProp>();
  const { userId, secondFactorTicket } = route.params;

  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [working, setWorking] = useState(false);
  const [resending, setResending] = useState(false);

  // Code is sent on arrival; the resend button covers delivery hiccups.
  useEffect(() => {
    securityService.sendLoginCode({ userId, secondFactorTicket });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    const ok = await securityService.sendLoginCode({ userId, secondFactorTicket });
    setResending(false);
    Alert.alert(ok ? 'Code sent' : 'Error', ok ? 'Check your messages.' : 'Could not send the code. Please try again.');
  };

  const handleVerify = async () => {
    if (working || code.trim().length !== 6) return;
    try {
      setWorking(true);
      const data = await securityService.verifyLoginCode({
        userId,
        code: code.trim(),
        secondFactorTicket,
        rememberDevice,
        deviceInfo: { devicePlatform: Platform.OS },
      });

      analytics.track(AnalyticsEvent.LoginSucceeded, { method: 'sms_2fa' });
      if (data.user?.id) analytics.identify(String(data.user.id));

      await setAuthToken(data.access_token);
      if (data.refresh_token) {
        await setRefreshToken(data.refresh_token);
      }
      await AsyncStorage.setItem('@user_id', data.user.id);
      await AsyncStorage.setItem('@user_email', data.user.email);
      await AsyncStorage.setItem('@email_verified', 'true');
      await AsyncStorage.setItem('@2fa_setup_complete', 'true');
      await AsyncStorage.setItem('hasLoggedIn', 'true');
      if (data.deviceTrustToken) {
        await setDeviceTrustToken(data.deviceTrustToken);
      }

      // An invitation opened before sign-in comes first.
      if (await routeToPendingInvitationIfAny(navigation)) return;

      const target = pickPostAuthTarget(data.user, { legacyOnboardingCompleted: true });
      if (target.kind === 'letterFlow') {
        navigation.reset({
          index: 0,
          routes: [{
            name: 'LetterFirst',
            state: { routes: [{ name: letterScreenForStep(target.initialStep) }] },
          }],
        });
      } else if (target.kind === 'wizard') {
        navigation.reset({
          index: 0,
          routes: [{
            name: 'OnboardingWizard',
            state: { routes: [{ name: wizardScreenForStep(target.initialStep) }] },
          }],
        });
      } else {
        navigation.reset({ index: 0, routes: [{ name: 'Dashboard' }] });
      }
    } catch (error: any) {
      logger.error('SMS verification failed:', error);
      Alert.alert('Error', error?.message || 'Invalid verification code.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.sheet}>
        <Text style={styles.title}>Enter your code</Text>
        <Text style={styles.subtitle}>
          We texted a 6-digit code to your phone. It expires in 10 minutes.
        </Text>

        <TextInput
          style={styles.input}
          placeholder="6-digit code"
          placeholderTextColor="#A0AEC0"
          keyboardType="number-pad"
          maxLength={6}
          value={code}
          onChangeText={setCode}
          autoFocus
          accessibilityLabel="Verification code"
        />

        <View style={styles.rememberRow}>
          <Text style={styles.rememberText}>Remember this device for 30 days</Text>
          <Switch
            value={rememberDevice}
            onValueChange={setRememberDevice}
            trackColor={{ false: '#D8DBDD', true: '#29BFC0' }}
            accessibilityLabel="Remember this device"
          />
        </View>

        <TouchableOpacity
          style={[styles.verifyButton, (working || code.trim().length !== 6) && styles.disabled]}
          onPress={handleVerify}
          disabled={working || code.trim().length !== 6}
          accessibilityRole="button"
          accessibilityLabel="Verify"
          accessibilityState={{ disabled: working || code.trim().length !== 6 }}
        >
          {working ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.verifyButtonText}>Verify</Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.resendButton}
          onPress={handleResend}
          disabled={resending}
          accessibilityRole="button"
          accessibilityLabel="Resend code"
        >
          <Text style={styles.resendText}>
            {resending ? 'Sending…' : "Didn't get it? Resend code"}
          </Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    marginTop: s(72),
    paddingTop: ms(28),
    paddingHorizontal: ms(20),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  subtitle: {
    fontSize: ms(15, 0.3),
    lineHeight: ms(22, 0.3),
    color: '#6B7280',
    marginBottom: ms(20),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(50),
    borderWidth: ms(1),
    borderColor: '#E5E9F0',
    paddingHorizontal: ms(18),
    paddingVertical: ms(14),
    fontSize: ms(20, 0.3),
    letterSpacing: 4,
    color: '#2C4364',
    textAlign: 'center',
    marginBottom: ms(16),
  },
  rememberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: ms(18),
  },
  rememberText: {
    flex: 1,
    fontSize: ms(14, 0.3),
    color: '#1C2C4F',
    fontWeight: '600',
  },
  verifyButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    alignItems: 'center',
  },
  verifyButtonText: {
    color: '#FFFFFF',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
  },
  resendButton: {
    alignItems: 'center',
    paddingVertical: ms(14),
  },
  resendText: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
  },
  disabled: {
    opacity: 0.5,
  },
});

export default SmsVerificationScreen;
